import { fetch, useApi } from '@omnipresentgroup/api';
import {
  type ContractorDocumentUploadResponse,
  DispositionEnum,
  type DocumentTypeEnum,
  type DocumentUploadRequest,
  type DocumentUploadUrlResponse,
  type DownloadDocumentResponse,
} from '@omnipresentgroup/types';
import { useCallback } from 'react';

import { API_BASE } from '../constants';

export const useContractorDocument = (documentType: DocumentTypeEnum) => {
  const { GET } = useApi();

  const uploadDocument = useCallback(
    async ({
      fileToUpload,
    }: DocumentUploadRequest): Promise<ContractorDocumentUploadResponse> => {
      // Get the presigned S3 URL and details
      const { url, fields, signature } = await GET<DocumentUploadUrlResponse>(
        `${API_BASE}/documents/upload-url`,
        {},
        { filename: fileToUpload.name, type: documentType },
      );

      // Upload the file to S3
      const headers = {
        Accept: 'application/json, text/plain, */*',
      };
      const formData = {
        ...fields,
        file: fileToUpload,
      };
      return fetch(url, {}, headers)
        .formData(formData)
        .post()
        .res()
        .then(() => {
          return {
            documentUrl: `${url}/${fields.key}`,
            fileName: fileToUpload.name,
            signature,
          };
        });
    },
    [GET, documentType],
  );

  const downloadDocument = useCallback(
    async ({ id }: { id: string }) => {
      return GET<DownloadDocumentResponse>(
        `${API_BASE}/documents/${id}/download`,
        {},
        {
          type: documentType,
          disposition: DispositionEnum.ATTACHMENT,
        },
      ).then(({ url }) => {
        window.open(url, '_self');
      });
    },
    [GET, documentType],
  );

  const viewDocument = useCallback(
    async ({ id }: { id: string }) => {
      return GET<DownloadDocumentResponse>(
        `${API_BASE}/documents/${id}/download`,
        {},
        {
          type: documentType,
          disposition: DispositionEnum.INLINE,
        },
      ).then(({ url }) => {
        window.open(url, '_blank')?.focus();
      });
    },
    [GET, documentType],
  );

  const deleteDocument = useCallback(() => {
    // No operation for now
  }, []);

  return {
    uploadDocument,
    downloadDocument,
    viewDocument,
    deleteDocument,
  };
};
